/**
 * 后端形状 → 界面形状的翻译层。
 *
 * # 为什么要有这一层，而不是让组件直接吃 wire 类型
 *
 * 因为两边的**缺失语义不一样**。后端的 `LuluAgent.model_status` 有七档，
 * 其中 `unset`／`unknown` 说的是「还没配」和「问不到」；
 * 而界面的 `TeamMember.state` 说的是「这个人此刻在干嘛」。
 * 把前者直接当后者渲染，会让一个「没配模型」的 Agent 显示成
 * 「状态不明，正在做某件事」——而它其实一件事都没做。
 *
 * 翻译写在这里而不是组件里，是为了让它有一个能红的落点。
 */
import type { ItemState } from './session-state'
import type { LuluAgent, WorkboardSnapshot } from './queries'
import type { ModelOption } from './model-picker'
import type { HumanMember, SubAgentRun, TeamMember } from './roster'
import type { WorkItemNode } from './panels'
import type { VarlessCopyId } from '@npc/copy'

/**
 * 工作项状态：后端九档 → 界面档。
 *
 * **认不出的回 null，绝不兜底成某一档。** 兜底成 `unknown` 看着无害，
 * 而它会把一个我们没见过的新状态显示成「不知道」，
 * 于是没有人会发现后端加了一档。
 */
export function itemStateFromWire(state: string): ItemState | null {
  switch (state) {
    case 'pending':
      return 'todo'
    case 'ready':
      return 'todo'
    case 'running':
      return 'run'
    case 'waiting':
      return 'wait'
    case 'blocked':
      return 'block'
    case 'recovering':
      return 'rec'
    case 'completed':
      return 'done'
    case 'failed':
      return 'fail'
    case 'cancelled':
      return 'closed'
    default:
      return null
  }
}

/**
 * Agent → 团队成员。
 *
 * `state` 恒为 `null`：**Agent 库里的一行说的是「这个人是谁」，
 * 不是「他此刻在干嘛」。** 后者要从工作板的 session 推，而那是另一条数据。
 * 在这里编一个 state 出来，就是拿身份信息冒充运行状态——
 * 与「假 running」是同一件事，只是来源换了。
 */
export function agentsToTeam(agents: readonly LuluAgent[]): readonly TeamMember[] {
  return agents
    .filter((a) => !a.disabled)
    .map((a) => ({
      kind: 'agent' as const,
      id: a.id,
      name: a.display_name,
      // 后端说 system，界面说 auto——两边各有各的词，这里是唯一的翻译点。
      nameSource: a.display_name_source === 'system' ? ('auto' as const) : ('user' as const),
      role: a.role ?? '',
      modelId: modelIdOf(a),
      state: null,
      scope: a.scope,
    }))
}

/**
 * 从 `model_policy` 里取模型 id。
 *
 * 它是 `additionalProperties: true` 的自由对象，所以这里**只认一个字段名
 * 并且认不出就回 null**——猜第二个字段名的那一刻，界面就开始显示
 * 一个可能不是模型 id 的字符串。
 */
/**
 * 「用什么模型」那一格显示什么。
 *
 * **`ok` 那档刻意不走模板**——那时候用户要看的是**哪一个模型**，
 * 不是一句「好着呢」。所以 ok 回 `{kind:'id'}`，界面用等宽字体把 id 打出来。
 *
 * 其余六档各自一条文案，**不合并**。判据是「用户看完之后要做的下一个动作」：
 * 「还没选」要他去选一个，「差一把 Key」要他去贴 Key，「这家还没接」要他换一家。
 * 并成一句「模型不可用」，他就得自己猜该干哪件。
 *
 * `ok` 但 policy 里没有 model_id 时回 `unknown`——后端说好着呢却说不出是哪个，
 * 那句「我说不清」比编一个 id 出来诚实。
 */
export type ModelCell =
  | { readonly kind: 'id'; readonly id: string }
  | { readonly kind: 'copy'; readonly id: VarlessCopyId }

export function modelCellFor(a: LuluAgent): ModelCell {
  const status = a.model_status
  if (status === 'ok') {
    const id = modelIdOf(a)
    return id === null ? { kind: 'copy', id: 'lulu_agents.model_unknown' } : { kind: 'id', id }
  }
  switch (status) {
    case 'unset':
      return { kind: 'copy', id: 'lulu_agents.model_unset' }
    case 'unavailable':
      return { kind: 'copy', id: 'lulu_agents.model_unavailable' }
    case 'rate_limited':
      return { kind: 'copy', id: 'lulu_agents.model_rate_limited' }
    case 'quota_exhausted':
      return { kind: 'copy', id: 'lulu_agents.model_quota_exhausted' }
    case 'disabled':
      return { kind: 'copy', id: 'lulu_agents.model_disabled' }
    case 'not_wired':
      return { kind: 'copy', id: 'lulu_agents.model_not_wired' }
    case 'unverified':
      return { kind: 'copy', id: 'lulu_agents.model_unverified' }
    case 'unlisted':
      return { kind: 'copy', id: 'lulu_agents.model_unlisted' }
    default:
      return { kind: 'copy', id: 'lulu_agents.model_unknown' }
  }
}

export function modelIdOf(a: LuluAgent): string | null {
  const id = a.model_profile_id
  if (typeof id === 'string' && id !== '') return id

  // 兼容迁移前的只读记录：新建和编辑只写稳定的 Model Profile UUID，
  // 但旧 Agent 在完成数据迁移前仍应诚实显示原 model_id，不能变成“未知”。
  const legacyID = (a.model_policy as { model_id?: unknown } | null)?.model_id
  return typeof legacyID === 'string' && legacyID !== '' ? legacyID : null
}

/**
 * 工作板 → 界面的树。
 *
 * 子 Agent（`sessions`）挂在它负责的工作项下面缩进显示，
 * **不单独成一行**——设计稿 S02 的形状。
 */
/**
 * 一次运行跑了多久。
 *
 * # 为什么之前是写死的 null
 *
 * 契约里那时没有开始时间，只有 `updated_at`。而 `updated_at` 是
 * 「上次有动静」——**一条卡住的活它不动**，拿它算耗时会停在那儿不涨，
 * 而那正是用户最需要看到它涨的时候。所以宁可显示横线。
 *
 * 现在服务端给了 `started_at` 与 `completed_at`（都可选，老版本不给）。
 *
 * # 两种数，一个函数
 *
 *   - 还在跑（没有 completed_at）：`now - started_at`，**每秒往上走**
 *   - 跑完了：`completed_at - started_at`，**定住**
 *
 * 少了第二种，一条早就结束的活会永远计时；少了第一种，
 * 用户看到的就是改之前那个样子——一个永远不动的横线。
 *
 * 拿不到 started_at（老服务端、字段被中间层删掉）时回 null，界面显示横线。
 * **不补 0**：0 秒是一句「它刚开始」的断言，而我们并不知道。
 */
export function elapsedOf(
  session: { readonly started_at?: string; readonly completed_at?: string | null },
  now: Date,
): number | null {
  if (!session.started_at) return null
  const start = Date.parse(session.started_at)
  if (!Number.isFinite(start)) return null
  const end = session.completed_at ? Date.parse(session.completed_at) : now.getTime()
  if (!Number.isFinite(end)) return null
  const ms = end - start
  // 负数只可能是两边的钟对不上。显示横线而不是 0——
  // 0 会被读成「刚开始」，而真相是「这个数不可信」。
  return ms >= 0 ? ms : null
}

export function workboardToNodes(snapshot: WorkboardSnapshot, now: Date): readonly WorkItemNode[] {
  const itemsById = new Map(requiredList(snapshot.work_items, 'work_items').map((item) => [item.id, item]))
  const sessionsById = new Map(requiredList(snapshot.sessions, 'sessions').map((session) => [session.id, session]))
  const byItem = new Map<string, SubAgentRun[]>()
  for (const s of requiredList(snapshot.sessions, 'sessions')) {
    // 只有绑到工作项上的 session 才是那一项下面的子 Agent。
    // 绑不上的**不挂到随便哪一项下面**——那会让用户看到一个
    // 「在做这件事」的执行单元，而它其实在做别的事。
    // **只有 subagent 才是临时子 Agent。** primary 是主 Agent 自己的
    // 运行时会话，把它也缩进画出来，团队里那位就会在工作板上
    // 变成自己的下属——而设计稿整节都在讲这两者不是一回事。
    if (s.session_role !== 'subagent') continue
    const itemId = s.delegated_work_item_id
    if (!itemId) continue
    const list = byItem.get(itemId) ?? []
    const parent = s.parent_session_id ? sessionsById.get(s.parent_session_id) : undefined
    list.push({
      kind: 'subrun',
      id: s.id,
      agentProfileId: s.agent_profile_id ?? null,
      name: '',
      nameSource: 'auto',
      // 职责由服务端的 session_role 给，解不出就留空——
      // resolveDisplayName 会据此回 null，界面则不显示名字，
      // 而不是把 uuid 或者 modelId 摆出来。
      responsibility: itemsById.get(itemId)?.title ?? '',
      modelId: null,
      parentAgentId: parent?.agent_profile_id ?? '',
      workItemId: itemId,
      state: itemStateFromWire(s.state),
      // 没有事件就是 null。**前端不得补 0**，补了就会画出一条
      // 停在最左边的进度条，而那是一句「它开始了」的断言。
      progress: null,
      elapsedMs: elapsedOf(s, now),
    })
    byItem.set(itemId, list)
  }
  // 谁在做这件活：item.assigned_profile_id → 名册里那位的 display_name。
  //
  // **名字来自名册，不来自 Agent 配置库。** 两者的分工见下面
  // workboardToTeam 那一大段：名册回答「这条会话里有谁」，
  // 配置库回答「账户里有哪些 Agent」。一个被停用的成员在配置库里可能已经
  // 不见了，而工作板上那件活仍然是他做的——用配置库查会把他显示成空白。
  const nameByProfile = new Map(
    requiredList(snapshot.roster, 'roster').map((m) => [m.agent_profile_id, m.display_name]),
  )
  return requiredList(snapshot.work_items, 'work_items').map((item) => ({
    id: item.id,
    name: item.title,
    // 查不到就是 null，界面不显示这一格。**不回退到 uuid**——
    // 一串 uuid 在「谁在做」这一栏里是一句用户读不懂的话，
    // 而空白至少是诚实的「不知道」。
    ownerName: (item.assigned_profile_id && nameByProfile.get(item.assigned_profile_id)) || null,
    // modelId **恒为 null，而且这是对的**。
    //
    // 2026-08-28 核过整份 WorkboardSnapshot 的 schema：WorkboardItem 与
    // WorkboardSession 上**都没有模型字段**。Session 上那个 runtime_adapter
    // 是「用哪个执行运行时」（agentbridge / openhands），不是模型——
    // 把它当模型显示是编一个数，而 v3 §3.4 逐字写着「缺失时显示 —，
    // 不能估算」。
    //
    // 要它有值得先在契约里加一个字段。登记在 tools/pending.json 的
    // b-workboard-model-id，那条一落地这一行就该跟着换。
    modelId: null,
    state: itemStateFromWire(item.state),
    progress: null,
    // **后端渲染好的那句原样带上来，前端不拼也不猜。**
    // 拿不到就是 null，界面少一句话——好过多一句错的。
    blockedReason: item.blocked_reason_text ?? null,
    // 撤回要用它。没有就是 null——非 development 的步骤本来就没有任务。
    taskId: item.task_id ?? null,
    subRuns: byItem.get(item.id) ?? [],
  }))
}

/**
 * Agent 库 → 输入框里可选的模型列表。
 *
 * **按 modelId 去重**：两个 Agent 用同一个模型是合法的（设计稿 W16 说过），
 * 但下拉里同一个模型出现两次，用户会以为它们是两个不同的东西。
 *
 * 没配模型的 Agent（`modelId === null`）整个跳过——它带不来一个可选项。
 */
export function agentsToModelOptions(agents: readonly LuluAgent[]): readonly ModelOption[] {
  const seen = new Set<string>()
  const out: ModelOption[] = []
  for (const a of agents) {
    const id = modelIdOf(a)
    if (id === null || seen.has(id)) continue
    seen.add(id)
    out.push({
      id,
      name: id,
      status: a.model_status,
      reason: a.model_status_reason ?? '',
    })
  }
  return out
}

/**
 * 会话 Session 的 14 档 → 界面档。
 *
 * 与 `itemStateFromWire` 分开写，**不是重复**：那一份映的是工作项的九档，
 * 这一份映的是执行会话的十四档，两套词表只是有几个词长得一样。
 * 合成一个函数的那天，后端给其中一边加一档，另一边会静默跟着变。
 *
 * `disconnected` 映成 `unknown` 而不是保留「上一次看到的样子」：
 * 断线时我们**确实不知道**它在干嘛，而设计稿整节都在讲这件事——
 * 连接断了就是断了，不许继续显示「进行中」。
 */
export function sessionStateForTeam(state: string): ItemState | null {
  switch (state) {
    case 'provisioning':
    case 'ready':
      return 'todo'
    case 'running':
      return 'run'
    case 'waiting_agent':
    case 'waiting_user':
      return 'wait'
    case 'blocked':
      return 'block'
    case 'recovering':
      return 'rec'
    case 'platform_held':
      return 'held'
    case 'disconnected':
      return 'unknown'
    case 'reassigned':
    case 'fenced':
    case 'cancelled':
      return 'closed'
    case 'completed':
      return 'done'
    case 'failed':
      return 'fail'
    default:
      // 认不出的回 null，绝不兜底成某一档——兜底会让后端新加的一档
      // 静默显示成「不知道」，于是没有人会发现它加了。
      return null
  }
}

/**
 * 当前团队 = 这条会话里**有主 Session 的那些 Agent**。
 *
 * # 为什么不是 Agent 库
 *
 * 上一版直接把 `GET /agents` 的结果当团队画，而库里的一行说的是
 * 「这个人是谁」，不是「他此刻在干嘛」——于是每一行的状态只能写
 * 「不知道」，而那正是线上被指出来的那个毛病。
 *
 * 内核里没有独立的「团队名册」表，团队就是这个投影：
 * `session_role === 'primary'` 的会话，按 `agent_profile_id` 关联到库里那一行。
 * 子 Agent 不算——**它们不进团队名册**（内核合同），那是工作板的事。
 *
 * 同时回「还没进来的」：设计稿 teamEmpty 的第二段「先加一位试试」列的就是它们。
 */
export function teamFromWorkboard(
  agents: readonly LuluAgent[],
  snapshot: WorkboardSnapshot,
): { readonly members: readonly TeamMember[]; readonly candidates: readonly TeamMember[] } {
  const library = agentsToTeam(agents)
  const byProfile = new Map<string, string>()
  for (const s of requiredList(snapshot.sessions, 'sessions')) {
    if (s.session_role !== 'primary') continue
    const profileId = s.agent_profile_id
    if (!profileId) continue
    // 同一个 Agent 有多条主 session 时取**最后看到的那条**：
    // 快照按 updated_at 给，后来的那条才是它此刻的样子。
    byProfile.set(profileId, s.state)
  }
  const members: TeamMember[] = []
  const candidates: TeamMember[] = []
  for (const member of library) {
    const wire = byProfile.get(member.id)
    if (wire === undefined) {
      candidates.push(member)
      continue
    }
    members.push({ ...member, state: sessionStateForTeam(wire) })
  }
  return { members, candidates }
}

// ───────────────────────────────────────────────────────────────────────
// 断口二：工作板投影里那三个新字段（roster / plans / presence）
//
// 交接文档 §2.2 的原话：这三样服务端在发，前端一行没读。
// 下面四个函数是那一行的落点，每一个都对着 openapi 生成的类型写，
// **一个手写类型都没有**（§3 硬约束：TS 客户端由 OpenAPI 生成）。
// ───────────────────────────────────────────────────────────────────────

/** 在线判据。**与 `state` 是两件事，不许合并**——理由见 `presenceOf`。 */
export type Presence = 'live' | 'unknown'

/**
 * 这条 session 此刻在不在线。
 *
 * # 为什么它不能和 `state` 合并
 *
 * 交接文档 §1.3 把代价说清了：
 *
 *   `state`    是 durable 状态机（写它要落 ledger、要动 fence），
 *              判错的代价是**打断一个正在干活的 Agent**；
 *   `presence` 只看「多久没听到它了」，不写任何 durable 事实，
 *              判错的代价只是界面多显示一次「状态未知」。
 *
 * PRD §13 #4 那句「强制断开 Agent 后 30 秒内显示断线」由 presence 满足，
 * **不是**由 state 满足——把 durable 阈值压到 30 秒以内，
 * 一次长模型调用挡住心跳就会把干活的 Agent 自己 fence 掉。
 *
 * # 客户端只许把 live 降成 unknown，绝不许反过来
 *
 * 服务端说 `live` 而快照已经放了 `staleAfterSeconds` 那么久，我们就把它
 * 降成 unknown：那是**更谨慎**，代价只是多显示一次「不知道」。
 *
 * 反过来（服务端说 unknown、客户端因为「刚拉到快照」判成 live）是
 * **客户端在断言一件它不知道的事**——而这一屏整条设计原则是「只画知道的」。
 * 所以这个函数没有那条分支，不是忘了写。
 */
export function presenceOf(
  session: { readonly presence?: Presence; readonly updated_at?: string },
  staleAfterSeconds: number,
  now: Date,
): Presence {
  if (session.presence !== 'live') return 'unknown'
  if (!session.updated_at) return 'unknown'
  const seen = Date.parse(session.updated_at)
  // 解不出时间就当不知道。**不要退回 live**——一个解析失败被当成
  // 「它还活着」，正是「假装知道」的最短路径。
  if (!Number.isFinite(seen)) return 'unknown'
  if (!Number.isFinite(staleAfterSeconds) || staleAfterSeconds <= 0) return 'live'
  return now.getTime() - seen > staleAfterSeconds * 1000 ? 'unknown' : 'live'
}

/**
 * 名册里的一位。
 *
 * **成员身份来自 `roster`，显示细节来自 Agent 配置库。** 两者的分工要说清：
 *
 *   roster  回答「这条会话里有谁」——**这是成员资格的唯一真相**
 *   配置库  回答「这位长什么样」（模型 id、名字是不是自动起的）
 *
 * 所以配置库里没有的那位照常列出（拿 roster 的 display_name），
 * 而配置库里有、roster 里没有的**一个都不列**——那才是上一版的毛病。
 */
export interface RosterMember extends TeamMember {
  /** 配置里已停用。**照常列出并标出来**——「他当时在队里」是既成事实。 */
  readonly disabled: boolean
  /** 在不在线。与 state 分开，见 `presenceOf`。 */
  readonly presence: Presence
  readonly seat: 'primary' | 'member'
}

/**
 * 「当前团队」= 这条会话的名册，**不是账户里有哪些 Agent**。
 *
 * 交接文档 §2.2：两者在单 Agent 账户上看起来一样，在有五个 Agent 的账户上
 * 完全不同——配置库回答「账户里有哪些 Agent」，roster 回答「这条会话里有谁」。
 *
 * 上一版是 `sessions × 配置库` 推出来的，于是：
 *   - 名册里有、但还没开 session 的那位**根本不显示**（他明明已经在队里）
 *   - 配置库里有、但不在这条会话里的那些会被当成「候选」——
 *     在五个 Agent 的账户上，那一列会列出四个与这条会话无关的人
 *
 * `disabled` 的成员照常列出：他当时确实在队里，把他抹掉等于改写历史。
 */
/**
 * 从快照里取一个**契约说必填、而实际可能不在**的数组。
 *
 * # 为什么需要它，以及它挡住的那次事故
 *
 * `WorkboardSnapshot` 的 required 里写着 roster 与 plans，所以这一层原来
 * 直接 `for (const m of snapshot.roster)`。2026-08-28 实测：
 * 少了那个字段时抛 `snapshot.roster is not iterable`，**整个 LuluScreen
 * 落到 error boundary**——聊天列、消息区、输入框一起没了。
 *
 * 也就是说：一个只影响右侧一栏显示的字段，能把整块工作台打掉。
 * 那个代价与它的用途完全不成比例。
 *
 * **不是「契约不可信所以到处加判断」**：契约仍然是判据，服务端不给就是
 * 服务端的毛病，所以这里出声（console.error 给部署的人，不是给用户看的文案）。
 * 但出声之后要继续渲染能渲染的部分——「名册读不到」和「工作台打不开」
 * 是两件严重程度差一个量级的事。
 */
function requiredList<T>(value: readonly T[] | undefined, field: string): readonly T[] {
  if (Array.isArray(value)) return value
  console.error(
    `[npc] 工作板快照里没有 ${field}，而契约把它列为必填。\n` +
      `  这一栏会显示成空的，工作台其余部分照常。\n` +
      `  多半是后端版本比契约旧，或者那条投影出错了——不是浏览器这一侧的问题。`,
  )
  return []
}

export function teamFromRoster(
  agents: readonly LuluAgent[],
  snapshot: WorkboardSnapshot,
  now: Date,
): { readonly members: readonly RosterMember[]; readonly candidates: readonly TeamMember[] } {
  const library = new Map(agentsToTeam(agents).map((m) => [m.id, m]))
  const live = new Map<string, { state: string; presence: Presence }>()
  for (const s of requiredList(snapshot.sessions, 'sessions')) {
    if (s.session_role !== 'primary') continue
    if (!s.agent_profile_id) continue
    // 同一位有多条主 session 时取**最后看到的那条**：快照按 updated_at 给。
    live.set(s.agent_profile_id, {
      state: s.state,
      presence: presenceOf(s, snapshot.presence_stale_after_seconds, now),
    })
  }
  const seated = new Set<string>()
  const members: RosterMember[] = []
  for (const m of requiredList(snapshot.roster, 'roster')) {
    seated.add(m.agent_profile_id)
    const shape = library.get(m.agent_profile_id)
    const running = live.get(m.agent_profile_id)
    members.push({
      kind: 'agent',
      id: m.agent_profile_id,
      // roster 的名字优先：它是**这条会话记下的那个名字**。
      // 配置库后来改名不该让历史上的名册跟着变。
      name: m.display_name ?? shape?.name ?? '',
      nameSource: shape?.nameSource ?? 'auto',
      role: shape?.role ?? '',
      modelId: shape?.modelId ?? null,
      scope: shape?.scope ?? 'account',
      state: running ? sessionStateForTeam(running.state) : null,
      // 没有 session 就是还没开工——那不是「失联」，但也不是「在线」。
      // 归到 unknown 而不是造第三档：界面上这两种都只说「不知道」。
      presence: running ? running.presence : 'unknown',
      disabled: m.disabled ?? false,
      seat: m.role,
    })
  }
  // 候选 = 配置库里**还没坐进这条会话**的那些。
  const candidates = [...library.values()].filter((m) => !seated.has(m.id))
  return { members, candidates }
}

/** 当前计划，以及它冻结的那份名册与现在的差距。 */
export interface CurrentPlan {
  readonly id: string
  readonly revision: number
  readonly status: 'proposed' | 'confirmed'
  readonly goal: string | null
  readonly collaborationMode: 'solo' | 'team'
  /** 这一版计划**是按几个人排的**（冻结时的名册大小）。 */
  readonly plannedFor: number
  /** 现在队里有几个。与上面那个不等时，界面要说得出来。 */
  readonly rosterNow: number
  /** 冻结名册里的那几位 id。**只有三个字段**，别按会话名册的形状渲染。 */
  readonly frozen: readonly {
    readonly agentProfileId: string
    readonly profileRevision: number
    readonly role: 'primary' | 'member' | null
  }[]
  /** 哪件活派给了谁：item_key → agent_profile_id。 */
  readonly assignment: Readonly<Record<string, string>>
}

/**
 * 当前那一版计划。
 *
 * 服务端按「最新 revision 在前，最多 8 条」给，所以取第一条。
 * **不自己重排**：排序是服务端的语义，客户端再排一次就是两份真相。
 *
 * 一条都没有回 null —— 那是真话（Lulu 还没提计划），不是错误。
 */
/**
 * 一起管这条会话的真人。
 *
 * ------------------------------------------------------------------
 * # 字段缺席是合法的，不出声
 *
 * 与 `requiredList` 那几个不同：`people` 是 2026-09-07 新加的字段，
 * 而浏览器可能正对着一台还没滚上去的服务端。**那种缺席不是毛病**，
 * 所以这里安静地当成空名单，不 console.error。
 *
 * 出声的门槛是「契约说必填而它不在」，那说明服务端坏了；
 * 这一条是「新字段还没到」，说明部署还在路上。两者要人做的下一个
 * 动作完全不同，混成一句会让每一次滚动发布都刷一屏假警报。
 *
 * # role 与 canLetAIAct 都原样接
 *
 * **不在这里按 role 反推 canLetAIAct**——那就是同一条规则的第二份实现，
 * 而放宽的那一份不会有任何东西会红。服务端那份在 team.Can(role, CapLetAIAct)。
 * ------------------------------------------------------------------
 */
export function peopleFromWorkboard(snapshot: WorkboardSnapshot): readonly HumanMember[] {
  const raw = (snapshot as { people?: readonly unknown[] }).people
  if (!Array.isArray(raw)) return []
  const out: HumanMember[] = []
  for (const row of raw) {
    const r = row as {
      user_id?: string
      display_name?: string
      role?: string
      can_let_ai_act?: boolean
      joined_at?: string
    }
    if (typeof r.user_id !== 'string' || r.user_id === '') continue
    const role =
      r.role === 'owner' || r.role === 'operator' || r.role === 'viewer' ? r.role : null
    out.push({
      kind: 'human',
      id: r.user_id,
      // 名字空着也照常列出来——**「这条会话里有他」是既成事实**，
      // 名字不好看不该让他从名单里消失。
      name: typeof r.display_name === 'string' && r.display_name !== '' ? r.display_name : r.user_id,
      role,
      canLetAIAct: r.can_let_ai_act === true,
      joinedAt: typeof r.joined_at === 'string' ? r.joined_at : '',
    })
  }
  return out
}

export function currentPlan(snapshot: WorkboardSnapshot): CurrentPlan | null {
  const plan = requiredList(snapshot.plans, 'plans')[0]
  if (!plan) return null
  return {
    id: plan.id,
    revision: plan.revision,
    status: plan.status,
    goal: plan.goal ?? null,
    collaborationMode: plan.collaboration_mode,
    plannedFor: requiredList(plan.roster, 'plans[].roster').length,
    rosterNow: requiredList(snapshot.roster, 'roster').length,
    frozen: requiredList(plan.roster, 'plans[].roster').map((m) => ({
      agentProfileId: m.agent_profile_id,
      profileRevision: m.profile_revision,
      role: m.role ?? null,
    })),
    assignment: plan.assignment,
  }
}

/**
 * 停用一个 Agent 会碰到什么。四档**分开**，不合成一个数字。
 *
 * 交接文档 §2.2 把理由写死了：四档要求用户做的下一个动作不一样——
 * 正在跑的要他安排接手；排队的会卡住；席位只是以后派不到它。
 * 合成一个「有 3 处受影响」，既可能是三件正在烧的活，
 * 也可能是三条早就结束的会话里的挂名。
 *
 * `blocking` 是「有没有正在进行的事」——它决定那颗确认按钮该不该出现，
 * 而不是决定要不要显示这些数字（数字永远显示）。
 */
export interface DisableImpact {
  readonly runningItems: number
  readonly pendingItems: number
  readonly activeSessions: number
  readonly rosterSeats: number
  readonly conversations: readonly string[]
  readonly conversationsTruncated: boolean
  /** 有正在跑的活或活着的会话 —— 服务端会回 409，界面要给「还是停用」。 */
  readonly blocking: boolean
}

export function disableImpactOf(raw: {
  readonly running_items: number
  readonly pending_items: number
  readonly active_sessions: number
  readonly roster_seats: number
  readonly conversations: readonly string[]
  readonly conversations_truncated?: boolean
}): DisableImpact {
  return {
    runningItems: raw.running_items,
    pendingItems: raw.pending_items,
    activeSessions: raw.active_sessions,
    rosterSeats: raw.roster_seats,
    conversations: raw.conversations,
    conversationsTruncated: raw.conversations_truncated ?? false,
    // 与服务端 409 的判据一致：正在跑的活或活着的会话。
    // **席位不算**——席位只是「以后派不到它」，不拦停用。
    blocking: raw.running_items > 0 || raw.active_sessions > 0,
  }
}

/**
 * 从 409 的 `details` 里解出影响面。
 *
 * # 为什么这里必须手写一个解析函数
 *
 * 契约上 **`impact` 只在 200 上有类型**（`AgentDisableImpact`）；
 * 409 回的是通用 `Error`，影响面塞在 `details?: { [key: string]: unknown }` 里。
 * 而 409 恰好才是「要用户点确认」的那条路——200 是不拦人直接停。
 *
 * 所以这不是在「手写类型」（§3 明令禁止的那件事），是在**边界上校验
 * 一份契约声明为 unknown 的数据**。两者的区别是：手写类型是替服务端
 * 声明它会给什么；这里是收到什么就验什么，验不过回 null。
 *
 * 已提请内核线把 409 的 details 定型，见
 * docs/contract-requests/工作台-2026-08-28-409的影响面没有类型.md。
 * 那条落地之后这个函数就该删掉，换成生成的类型。
 *
 * **验不过回 null 而不是回一个零值。** 一份四档全 0 的影响面
 * 说的是「停用不会打断任何东西」——那是一句会让人放心点下去的话，
 * 而我们此刻并不知道它对不对。
 */
export function parseDisableImpact(details: unknown): DisableImpact | null {
  if (typeof details !== 'object' || details === null) return null
  const d = details as Record<string, unknown>
  const num = (k: string): number | null => {
    const v = d[k]
    return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null
  }
  const running = num('running_items')
  const pending = num('pending_items')
  const sessions = num('active_sessions')
  const seats = num('roster_seats')
  // 四档缺任何一档都回 null：显示三档而把第四档当 0，
  // 正是「一个悄悄变短的列表被当成完整清单」那个形状。
  if (running === null || pending === null || sessions === null || seats === null) return null
  const raw = d['conversations']
  const conversations = Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : []
  return disableImpactOf({
    running_items: running,
    pending_items: pending,
    active_sessions: sessions,
    roster_seats: seats,
    conversations,
    // 契约上 conversations 最多 32 条，而**那个上限在 TS 里看不见**
    // （生成物只有 string[]，maxItems 被抹掉）。所以只信这个布尔，
    // 不拿数组长度去推断有没有被截断。
    conversations_truncated: d['conversations_truncated'] === true,
  })
}
